import { Link, useLocation } from "react-router-dom";
import { Home, Briefcase, Clipboard, GraduationCap, Bell } from "lucide-react";
import { useTheme } from "../../hooks/useTheme";
import { useNotifications } from "../../hooks/useNotifications";

/**
 * Mobile-only bottom navigation bar for regular (job-seeking) users.
 * Hidden from tablet width up (md:hidden); App.js decides when to render it.
 * Reuses the same routes as the desktop Sidebar so nothing new is wired.
 */
export default function BottomNav() {
    const { isDarkMode } = useTheme();
    const location = useLocation();
    const { unreadCount } = useNotifications();

    const items = [
        { to: "/userdashboard", label: "Home", Icon: Home, isActive: (p) => p === "/userdashboard" },
        { to: "/jobbrowser", label: "Jobs", Icon: Briefcase, isActive: (p) => p.startsWith("/jobbrowser") || p.startsWith("/jobListings") },
        { to: "/applicationsTracker", label: "Tracker", Icon: Clipboard, isActive: (p) => p.startsWith("/applicationsTracker") },
        { to: "/coaching", label: "Coaching", Icon: GraduationCap, isActive: (p) => p.startsWith("/coaching") },
        { to: "/notifications", label: "Alerts", Icon: Bell, badge: unreadCount, isActive: (p) => p.startsWith("/notifications") },
    ];

    return (
        <nav
            className={`md:hidden fixed left-4 right-4 z-30 flex justify-around items-stretch rounded-full border shadow-lg ${
                isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"
            }`}
            style={{ bottom: "calc(env(safe-area-inset-bottom) + 12px)" }}
        >
            {items.map(({ to, label, Icon, badge, isActive }) => {
                const active = isActive(location.pathname);
                const activeColor = isDarkMode ? "text-blue-400" : "text-blue-600";
                const idleColor = isDarkMode ? "text-slate-400" : "text-slate-500";
                return (
                    <Link
                        key={to}
                        to={to}
                        aria-label={label}
                        aria-current={active ? "page" : undefined}
                        className={`relative flex flex-1 flex-col items-center justify-center gap-1 py-2 no-underline transition-colors ${
                            active ? activeColor : idleColor
                        }`}
                    >
                        <span className="relative">
                            <Icon size={24} strokeWidth={active ? 2.4 : 2} />
                            {badge > 0 && (
                                <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
                                    {badge > 9 ? "9+" : badge}
                                </span>
                            )}
                        </span>
                        <span className="text-[10px] font-medium leading-none">{label}</span>
                    </Link>
                );
            })}
        </nav>
    );
}
